/**
 * Termin-Einladung aus dem Mailtext lesen (#29).
 *
 * Gemeldet am 24.08.2026 von Marlene: „Hier habe ich jetzt keine Option einen
 * Termin anzulegen" — zu einer Mail, die das Portal sichtbar als
 * TERMIN-EINLADUNG erkannt hatte (Teams-Besprechung, Datum und Uhrzeit standen
 * im Kasten). Angeboten wurden nur Antworten / Weiterleiten / Verschieben.
 *
 * Paul im Thread: „Wenn du dir einen Plan erstellen lässt schon". Genau das ist
 * der Umweg — die Aktionen entstehen erst mit einem Plan, und dass man den
 * erst anfordern muss, weiß niemand, der es nicht gesagt bekommt.
 *
 * Der Block wird beim IMAP-Sync aus dem text/calendar-Part gebaut
 * (`services/imap/_body.py:_extract_ical_invitations`) und steht im
 * `body_text`:
 *
 *     [TERMIN-EINLADUNG: Arman x Tobias
 *     Wann: Mi., 26.08.2026 15:00–16:00
 *     Wo: Microsoft Teams-Besprechung
 *     Organisator: tobias.probefeld@partner-beispiel.example]
 *
 * 🔑 Gelesen wird aus dem gespeicherten Text, nicht aus einer neuen Spalte —
 * damit gilt es auch fuer alle Mails, die schon da sind.
 */

export type TerminEinladung = {
  /** TERMIN-EINLADUNG | TERMIN-ABSAGE | TERMIN-ANTWORT | TERMIN */
  art: string
  titel: string
  /** Lokale Startzeit als `YYYY-MM-DDTHH:mm` — direkt fuer datetime-local. */
  start: string | null
  /** Dauer in Stunden, wenn eine Endzeit am selben Tag dabeisteht. */
  dauerStunden: number | null
  ort: string | null
  organisator: string | null
}

const BLOCK = /\[(TERMIN-EINLADUNG|TERMIN-ABSAGE|TERMIN-ANTWORT|TERMIN):\s*([^\n\]]*)([^\]]*)\]/

/** „Mi., 26.08.2026 15:00–16:00" → Start + Dauer. */
function zeitenLesen(wann: string): { start: string | null; dauerStunden: number | null } {
  const m = wann.match(/(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2})(?:\s*[–-]\s*(\d{2}):(\d{2}))?/)
  if (!m) return { start: null, dauerStunden: null }
  const [, tt, mm, jjjj, h1, min1, h2, min2] = m
  const start = `${jjjj}-${mm}-${tt}T${h1}:${min1}`
  if (!h2) return { start, dauerStunden: null }
  const minuten = (Number(h2) * 60 + Number(min2)) - (Number(h1) * 60 + Number(min1))
  // Endzeit vor Startzeit heisst: der Termin geht ueber Mitternacht oder ueber
  // mehrere Tage. Dann lieber keine Dauer vorbelegen als eine falsche.
  return { start, dauerStunden: minuten > 0 ? minuten / 60 : null }
}

function feld(rest: string, name: string): string | null {
  const m = rest.match(new RegExp(`^${name}:\\s*(.+)$`, "m"))
  return m ? m[1].trim() : null
}

export function terminEinladungLesen(bodyText?: string | null): TerminEinladung | null {
  if (!bodyText) return null
  const m = bodyText.match(BLOCK)
  if (!m) return null
  const [, art, titel, rest] = m
  const wann = feld(rest, "Wann") || ""
  const { start, dauerStunden } = zeitenLesen(wann)
  return {
    art,
    titel: (titel || "").trim() || "Termin",
    start,
    dauerStunden,
    ort: feld(rest, "Wo"),
    organisator: feld(rest, "Organisator"),
  }
}

/**
 * Nur eine echte Einladung ist uebernehmbar.
 *
 * Bei einer Absage waere ein Knopf „Termin uebernehmen" schlicht falsch, und
 * eine Antwort („Zusage von X") ist kein Termin, den man anlegt.
 */
export function istUebernehmbar(e: TerminEinladung | null): boolean {
  return !!e && !!e.start && (e.art === "TERMIN-EINLADUNG" || e.art === "TERMIN")
}

/** Passende Auswahl fuer das Dauer-Feld des Buchen-Dialogs. */
export function dauerAuswahl(e: TerminEinladung | null): string {
  const stunden = e?.dauerStunden
  if (!stunden) return "1"
  const erlaubt = [1, 1.5, 2, 3, 4, 6, 8]
  const naechste = erlaubt.reduce((a, b) =>
    Math.abs(b - stunden) < Math.abs(a - stunden) ? b : a)
  return String(naechste)
}
